import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, EditOutlined, ExpandLess, ExpandMore, Schedule,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import {
  attendanceApi, AttendanceDay, AttendancePunch, AttendanceReport, AttendanceStaffRow, ShiftDay,
} from '../../services/attendanceApi';
import {
  RangeMode, WEEKDAYS, canEditAttendance, dateLabel, hhmmLabel, hm, istTime, istToday,
  rangeFor, rangeLabel, shiftAnchor,
} from './attendanceFormat';
import ShiftEditorDialog from './ShiftEditorDialog';
import EditPunchDialog from './EditPunchDialog';

interface Props {
  /** Required for super admins; ignored by the server for studio logins. */
  studioId?: string;
  /** Show only this instructor (the Instructor performance tab). */
  instructorId?: string;
  /** Logged-in role — decides whether shifts/punches can be edited. */
  role?: string;
  /** Bump to refetch (e.g. after settings change). */
  refreshKey?: number;
}

const STATUS_CHIP: Record<AttendanceDay['status'], { label: string; color: string; bg: string }> = {
  present: { label: 'Present', color: '#1b5e20', bg: '#e8f5e9' },
  absent: { label: 'Absent', color: '#b71c1c', bg: '#ffebee' },
  off: { label: 'Day off', color: '#616161', bg: '#f1f1f1' },
  upcoming: { label: 'Upcoming', color: '#616161', bg: '#f7f7f7' },
};

const Flag: React.FC<{ label: string; tone: 'bad' | 'good' | 'info' | 'warn' }> = ({ label, tone }) => {
  const map = {
    bad: { c: '#b71c1c', b: '#ffebee' },
    warn: { c: '#8a5300', b: '#fff4e0' },
    good: { c: '#1b5e20', b: '#e8f5e9' },
    info: { c: '#0d47a1', b: '#e3f2fd' },
  }[tone];
  return <Chip size="small" label={label} sx={{ color: map.c, bgcolor: map.b, fontWeight: 600, height: 22 }} />;
};

const dayFlags = (d: AttendanceDay) => (
  <Box display="flex" gap={0.5} flexWrap="wrap">
    {d.lateMinutes > 0 && <Flag tone="bad" label={`Late ${hm(d.lateMinutes)}`} />}
    {d.earlyArrivalMinutes > 0 && <Flag tone="good" label={`Early ${hm(d.earlyArrivalMinutes)}`} />}
    {d.leftEarlyMinutes > 0 && <Flag tone="warn" label={`Left early ${hm(d.leftEarlyMinutes)}`} />}
    {d.overtimeMinutes > 0 && <Flag tone="info" label={`Overtime ${hm(d.overtimeMinutes)}`} />}
    {d.missedPunchOut && <Flag tone="warn" label="Missed punch-out" />}
    {d.openNow && <Flag tone="info" label="In now" />}
  </Box>
);

const shiftSummary = (days: ShiftDay[]) => {
  if (!days.length) return 'No shift set';
  const groups = new Map<string, number[]>();
  for (const d of days) {
    const k = `${hhmmLabel(d.start)}–${hhmmLabel(d.end)}`;
    groups.set(k, [...(groups.get(k) ?? []), d.weekday]);
  }
  return Array.from(groups.entries())
    .map(([k, wds]) => `${wds.sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((w) => WEEKDAYS[w]).join(', ')} ${k}`)
    .join(' · ');
};

/**
 * Staff attendance report: hours, first in / last out, late, early arrival,
 * left early, overtime, absences and missed punch-outs, per person and per day.
 * Every figure comes from the server; this only formats it.
 */
const AttendanceReportView: React.FC<Props> = ({ studioId, instructorId, role, refreshKey }) => {
  const [mode, setMode] = useState<RangeMode>('week');
  const [anchor, setAnchor] = useState(istToday());
  const [custom, setCustom] = useState(() => rangeFor('week', istToday()));
  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [shiftFor, setShiftFor] = useState<AttendanceStaffRow | null>(null);
  const [suggested, setSuggested] = useState<Record<string, { start: string; end: string } | null>>({});
  const [editing, setEditing] = useState<{ punch: AttendancePunch; name: string } | null>(null);

  const canEdit = canEditAttendance(role);
  const single = !!instructorId;
  const range = useMemo(() => (mode === 'custom' ? custom : rangeFor(mode, anchor)), [mode, anchor, custom]);

  const load = useCallback(async () => {
    if (role && ['super_admin', 'admin'].includes(role) && !studioId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await attendanceApi.getReport({ ...range, studioId, instructorId });
      setReport(r);
      if (r.staff.length === 1) setOpen({ [r.staff[0].studioUserId]: true });
    } catch (e: any) {
      setError(e?.message || 'Could not load attendance');
    } finally {
      setLoading(false);
    }
  }, [range, studioId, instructorId, role]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Instructor shift pre-fill hints (from their existing single shift).
  useEffect(() => {
    if (!canEdit) return;
    attendanceApi
      .getStaff(studioId)
      .then((r) => setSuggested(Object.fromEntries(r.staff.map((s) => [s.studioUserId, s.suggestedShift]))))
      .catch(() => undefined);
  }, [canEdit, studioId, refreshKey]);

  const customValid = mode !== 'custom' || (custom.from && custom.to && custom.from <= custom.to);

  return (
    <Box>
      {/* Range */}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="day" sx={{ px: 1.5 }}>Day</ToggleButton>
          <ToggleButton value="week" sx={{ px: 1.5 }}>Week</ToggleButton>
          <ToggleButton value="month" sx={{ px: 1.5 }}>Month</ToggleButton>
          <ToggleButton value="custom" sx={{ px: 1.5 }}>Custom</ToggleButton>
        </ToggleButtonGroup>
        {mode === 'custom' ? (
          <>
            <TextField
              type="date" size="small" label="From" value={custom.from}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date" size="small" label="To" value={custom.to}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </>
        ) : (
          <Box display="flex" alignItems="center">
            <IconButton size="small" onClick={() => setAnchor((a) => shiftAnchor(mode, a, -1))}><ChevronLeft /></IconButton>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 190, textAlign: 'center' }}>
              {rangeLabel(mode, range.from, range.to)}
            </Typography>
            <IconButton size="small" onClick={() => setAnchor((a) => shiftAnchor(mode, a, 1))}><ChevronRight /></IconButton>
            <Button size="small" onClick={() => setAnchor(istToday())}>Today</Button>
          </Box>
        )}
        {loading && <CircularProgress size={18} />}
      </Box>

      {!customValid && <Alert severity="warning" sx={{ mb: 2 }}>Choose a start date on or before the end date.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {report && !report.settings.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Staff attendance is off for this studio, so staff can't punch in yet. Contact SweatSocial to turn it on.
        </Alert>
      )}
      {report?.instructorHasNoLogin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This instructor doesn't have a partner app login yet, so they can't punch in. Add them under Staff with the
          same phone number.
        </Alert>
      )}
      {report && !report.staff.length && !report.instructorHasNoLogin && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No instructors or managers with a partner app login at this studio.
        </Typography>
      )}

      {/* People */}
      <Box display="flex" flexDirection="column" gap={2}>
        {report?.staff.map((s) => {
          const t = s.totals;
          const isOpen = !!open[s.studioUserId];
          return (
            <Card key={s.studioUserId} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Box flexGrow={1} minWidth={200}>
                    {!single && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight={700}>{s.name}</Typography>
                        <Chip size="small" label={s.role === 'manager' ? 'Manager' : 'Instructor'} sx={{ height: 20 }} />
                        {s.removed && <Chip size="small" label="Removed" sx={{ height: 20 }} />}
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                      <Schedule sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{shiftSummary(s.weeklyShift)}</Typography>
                      {canEdit && !s.removed && (
                        <Button size="small" onClick={() => setShiftFor(s)} sx={{ ml: 0.5 }}>
                          {s.weeklyShift.length ? 'Edit shift' : 'Set shift'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {!single && (
                    <IconButton
                      onClick={() => setOpen((o) => ({ ...o, [s.studioUserId]: !o[s.studioUserId] }))}
                      aria-label={isOpen ? 'Hide days' : 'Show days'}
                    >
                      {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </Box>

                {/* Totals */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
                    gap: 1.5,
                    mt: 1.5,
                  }}
                >
                  {[
                    ['Hours worked', hm(t.totalMinutes), t.scheduledMinutes ? `of ${hm(t.scheduledMinutes)} scheduled` : ''],
                    ['Present', String(t.presentDays), t.absentDays ? `${t.absentDays} absent` : 'days'],
                    ['Late', t.lateDays ? `${t.lateDays}×` : '0', t.lateMinutes ? hm(t.lateMinutes) : ''],
                    ['Early arrival', t.earlyArrivalDays ? `${t.earlyArrivalDays}×` : '0', t.earlyArrivalMinutes ? hm(t.earlyArrivalMinutes) : ''],
                    ['Left early', t.leftEarlyDays ? `${t.leftEarlyDays}×` : '0', t.leftEarlyMinutes ? hm(t.leftEarlyMinutes) : ''],
                    ['Overtime', t.overtimeMinutes ? hm(t.overtimeMinutes) : '0', t.overtimeDays ? `${t.overtimeDays} days` : ''],
                    ['Missed punch-outs', String(t.missedPunchOuts), ''],
                  ].map(([label, value, sub]) => (
                    <Box key={label} sx={{ bgcolor: colors.backgroundTertiary, borderRadius: 1.5, px: 1.5, py: 1 }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
                    </Box>
                  ))}
                </Box>

                {/* Days */}
                <Collapse in={single || isOpen} unmountOnExit>
                  <Box sx={{ overflowX: 'auto', mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Shift</TableCell>
                          <TableCell>In</TableCell>
                          <TableCell>Out</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Punches</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {s.days
                          .filter((d) => d.status !== 'upcoming' || d.punches.length)
                          .slice()
                          .reverse()
                          .map((d) => {
                            const st = STATUS_CHIP[d.status];
                            return (
                              <TableRow key={d.date} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateLabel(d.date)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                  {d.shift ? `${hhmmLabel(d.shift.start)}–${hhmmLabel(d.shift.end)}` : '—'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{istTime(d.firstIn)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.lastOut ? istTime(d.lastOut) : d.openNow ? 'In now' : '—'}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{d.punches.length ? hm(d.totalMinutes) : '—'}</TableCell>
                                <TableCell>
                                  <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                                    <Chip size="small" label={st.label} sx={{ color: st.color, bgcolor: st.bg, fontWeight: 600, height: 22 }} />
                                    {dayFlags(d)}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" flexDirection="column" gap={0.25}>
                                    {d.punches.map((p) => (
                                      <Box key={p._id} display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="caption">
                                          {istTime(p.inAt)} → {p.outAt ? istTime(p.outAt) : p.status === 'open' ? 'now' : 'no punch-out'}
                                          {p.durationMinutes != null && ` · ${hm(p.durationMinutes)}`}
                                        </Typography>
                                        {p.edited && (
                                          <Tooltip title={`Edited by ${p.edited.by}${p.edited.note ? `: ${p.edited.note}` : ''}`}>
                                            <Chip size="small" label="Edited" sx={{ height: 18, fontSize: 10 }} />
                                          </Tooltip>
                                        )}
                                        {canEdit && p.status !== 'open' && (
                                          <IconButton size="small" onClick={() => setEditing({ punch: p, name: s.name })} aria-label="Correct punch">
                                            <EditOutlined sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {report && (
        <Typography variant="caption" color="text.disabled" display="block" mt={2}>
          Arriving or leaving within {report.settings.graceMinutes} minutes of the shift counts as on time. Late and early
          show the full difference (e.g. 9:43 for a 9:00 shift is 43 minutes late). Missed punch-outs add no hours until
          corrected.
        </Typography>
      )}

      {shiftFor && (
        <ShiftEditorDialog
          open
          staffName={shiftFor.name}
          studioUserId={shiftFor.studioUserId}
          studioId={studioId}
          days={shiftFor.weeklyShift}
          suggested={suggested[shiftFor.studioUserId] ?? null}
          onClose={() => setShiftFor(null)}
          onSaved={() => { setShiftFor(null); load(); }}
        />
      )}
      <EditPunchDialog
        punch={editing?.punch ?? null}
        staffName={editing?.name ?? ''}
        studioId={studioId}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </Box>
  );
};

export default AttendanceReportView;
